import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const EMPTY = { name: "", price: "", days: 30, description: "", active: true };

export default function Planos() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["admin", "gerente"].includes(user.role);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: plans, isLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : EMPTY);
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Nome e valor são obrigatórios");
      return;
    }
    setSaving(true);
    const payload = { ...form, price: parseFloat(String(form.price).replace(",", ".")) || 0, days: parseInt(form.days) || 30 };
    try {
      if (editing) await api.put(`/plans/${editing.id}`, payload);
      else await api.post("/plans", payload);
      toast.success(editing ? "Plano atualizado" : "Plano cadastrado");
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/plans/${p.id}`);
      toast.success("Plano removido");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="planos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Planos</h1>
          <p className="text-sm text-muted-foreground">Mensalidades e pacotes da academia</p>
        </div>
        {canEdit && (
          <Button data-testid="new-plan-btn" onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" /> Novo plano
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="plans-grid">
          {(plans || []).map((p) => (
            <div key={p.id} className="rounded-xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.days} dias de acesso</div>
                </div>
                <Badge variant="outline" className={p.active ? "border-emerald-500/40 text-emerald-500" : "text-muted-foreground"}>
                  {p.active ? "Ativo" : "Inativo"}
                </Badge>
              </div>
              <div className="num mt-4 text-3xl font-bold text-emerald-500">{brl(p.price)}</div>
              {p.description && <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>}
              <div className="mt-3 text-xs text-muted-foreground">
                ≈ {brl(p.price / Math.max(1, Math.round(p.days / 30.42)))}/mês
              </div>
              {canEdit && (
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" data-testid={`edit-plan-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }}>
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Editar
                  </Button>
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-500" data-testid={`delete-plan-${p.id}`} onClick={() => remove(p)}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Remover
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar plano" : "Novo plano"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="plan-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Mensal" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Valor (R$) *</Label>
                <Input data-testid="plan-price" inputMode="decimal" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="num" />
              </div>
              <div className="space-y-1.5">
                <Label>Duração (dias)</Label>
                <Input type="number" data-testid="plan-days" value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))} className="num" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descrição</Label>
              <Input data-testid="plan-description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <Label>Ativo</Label>
              <Switch data-testid="plan-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            <Button data-testid="plan-save" onClick={save} disabled={saving} className="w-full h-11">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
